import { getClassLevel } from './classIdentity.mjs';
export const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'];
const names = {
  strength: 'str',
  dexterity: 'dex',
  constitution: 'con',
  intelligence: 'int',
  wisdom: 'wis',
  charisma: 'cha'
};
export const featKey = feat => String(feat?.featName ?? feat ?? '').trim().toLowerCase().replace(/[’']/g, '').replace(/[\s_-]+/g, ' ');
export const isASI = feat => ['stat increase', 'ability score improvement', 'ability score increase'].includes(featKey(feat));
export function earnedFeatSlots(character = {}) {
  return (character.occupation || []).reduce((n, o) => {
    const level = Number(o.Level ?? o.level) || 0,
      name = String(o.class ?? o.Occupation ?? o.Name ?? o.name ?? '').trim().toLowerCase();
    return n + [4, 8, 12, 16, 19].filter(v => v <= level).length + (name === 'fighter' ? [6, 14].filter(v => v <= level).length : 0) + (name === 'rogue' && level >= 10 ? 1 : 0);
  }, 0);
}
export const asiSlots = character => Math.ceil(ABILITIES.reduce((n, a) => n + Math.max(0, Number(character?.abilityScoreImprovement?.[a]) || 0), 0) / 2);
export const classFeat = feat => !['background', 'race', 'species', 'class-feature'].includes(feat?._featSelection?.source);
export const remainingFeatSlots = character => earnedFeatSlots(character) - asiSlots(character) - (character.feat || []).filter(f => featKey(f) && classFeat(f)).length;
export const permanentAbilityScore = (character, ability) => Number(character?.[ability] || 0) + Number(character?.race?.abilities?.[ability] || 0) + Number(character?.abilityScoreImprovement?.[ability] || 0) + (character?.feat || []).reduce((n, f) => n + Number(f?.[ability] || 0), 0);
export const ASI_DEFINITION = {
  featName: 'Stat Increase',
  notes: 'Increase one ability by 2, or two abilities by 1, to a maximum of 20.',
  minLevel: 4,
  repeatable: true,
  abilityIncreaseOptions: [{
    abilities: ABILITIES,
    amount: 1
  }, {
    abilities: ABILITIES,
    amount: 1
  }]
};
export function featRules(definition = {}) {
  const key = featKey(definition),
    rules = {
      ...definition
    };
  if (isASI(definition)) return {
    ...ASI_DEFINITION,
    featName: definition.featName || ASI_DEFINITION.featName
  };
  if (String(rules.category || '').toLowerCase() === 'general') rules.minLevel = Math.max(4, Number(rules.minLevel) || 0);
  if (key === 'grappler') Object.assign(rules, Object.fromEntries(ABILITIES.map(a=>[a,0])), {
    prerequisites: null,
    prerequisite: null,
    requirements: null,
    repeatable: false,
    minLevel: 4,
    anyAbilityScores: {
      str: 13,
      dex: 13
    },
    abilityIncreaseOptions: [{
      abilities: ['str', 'dex'],
      amount: 1
    }]
  });
  if (['skilled', 'magic initiate'].includes(key)) rules.repeatable = true;
  if (key === 'skilled') rules.skillChoiceCount = 3;
  if (String(rules.category || '').toLowerCase() === 'epic boon' || key.startsWith('boon of ')) rules.minLevel = Math.max(19, Number(rules.minLevel) || 0);
  if (['archery', 'defense', 'great weapon fighting', 'two weapon fighting'].includes(key)) { rules.requiresFightingStyle = true; rules.repeatable = false; }
  return rules;
}
export function featEligibility(character, definition, {
  ignoreSlots = false
} = {}) {
  const d = featRules(definition),
    key = featKey(d),
    owned = (character.feat || []).filter(f => featKey(f) === key || isASI(f) && isASI(d));
  const repeatable = d.repeatable === true || isASI(d);
  if(d.requiresFightingStyle && ['fighter','ranger','paladin'].some(classId=>character.classState?.[classId]?.fightingStyle?.id===key.replace(/ /g,'-')))return {allowed:false,reason:'This Fighting Style is already granted by your class.',owned:true};
  if (owned.length && !repeatable) return {
    allowed: false,
    reason: 'This feat is not repeatable.',
    owned: true
  };
  const level = (character.occupation || []).reduce((n, o) => n + Number(o.Level ?? o.level ?? 0), 0);
  const min = Number(d.minLevel ?? d.requiredLevel ?? 0);
  if (level < min) return {
    allowed: false,
    reason: `Requires level ${min}.`
  };
  if (d.requiresFightingStyle && !(getClassLevel(character, 'fighter') >= 1 || getClassLevel(character, 'paladin') >= 2 || getClassLevel(character, 'ranger') >= 2)) return {
    allowed: false,
    reason: 'Requires the Fighting Style feature.'
  };
  const requirements = d.abilityPrerequisites || d.minAbilityScores || {};
  if (Object.entries(requirements).some(([a, min]) => !ABILITIES.includes(names[a.toLowerCase()] || a) || permanentAbilityScore(character, names[a.toLowerCase()] || a) < Number(min))) return {
    allowed: false,
    reason: 'Ability score prerequisite not met.'
  };
  if (d.anyAbilityScores && !Object.entries(d.anyAbilityScores).some(([a, min]) => permanentAbilityScore(character, names[a.toLowerCase()] || a) >= Number(min))) return {
    allowed: false,
    reason: `Requires ${Object.entries(d.anyAbilityScores).map(([a, n]) => `${a.toUpperCase()} ${n}`).join(' or ')}.`
  };
  const requiredClass = d.requiredClass || d.classPrerequisite;
  if (requiredClass && !getClassLevel(character, requiredClass)) return {
    allowed: false,
    reason: `Requires a level in ${requiredClass}.`
  };
  if (d.requiresSpellcasting && !(character.occupation || []).some(o => ['bard', 'cleric', 'druid', 'sorcerer', 'warlock', 'wizard', 'paladin', 'ranger'].some(c => getClassLevel({
    occupation: [o]
  }, c) >= 1))) return {
    allowed: false,
    reason: 'Requires Spellcasting or Pact Magic.'
  };
  const raw = d.prerequisites ?? d.prerequisite ?? d.requirements;
  for (const text of raw == null ? [] : Array.isArray(raw) ? raw : [raw]) {
    if (typeof text !== 'string') return {
      allowed: false,
      reason: 'This feat needs structured prerequisite data before it can be selected.'
    };
    let normalized = text.toLowerCase().replace(/prerequisites?:?/g, '').trim();
    if (/spellcasting|pact magic/.test(normalized)) {
      if (!featEligibility(character, {
        requiresSpellcasting: true
      }, {
        ignoreSlots: true
      }).allowed) return {
        allowed: false,
        reason: 'Requires Spellcasting or Pact Magic.'
      };
      normalized = normalized.replace(/spellcasting(?: or pact magic)?(?: feature)?|pact magic(?: feature)?/g, '');
    }
    if (/fighting style/.test(normalized)) {
      if (!featEligibility(character, {
        requiresFightingStyle: true
      }, {
        ignoreSlots: true
      }).allowed) return {
        allowed: false,
        reason: 'Requires the Fighting Style feature.'
      };
      normalized = normalized.replace(/fighting style(?: feature)?/g, '');
    }
    if (!normalized || /^none\.?$/.test(normalized)) continue;
    const levelMatch = normalized.match(/(?:level\s*)(\d+)/);
    if (levelMatch && level < Number(levelMatch[1])) return {
      allowed: false,
      reason: `Requires level ${levelMatch[1]}.`
    };
    const pairs = [...normalized.matchAll(/(strength|dexterity|constitution|intelligence|wisdom|charisma|str|dex|con|int|wis|cha)\s*(\d+)/g)];
    if (pairs.length) {
      const results = pairs.map(m => permanentAbilityScore(character, names[m[1]] || m[1]) >= Number(m[2]));
      if (/\bor\b/.test(normalized) ? !results.some(Boolean) : !results.every(Boolean)) return {
        allowed: false,
        reason: `Requires ${text}.`
      };
    }
    const residual = normalized.replace(/level\s*\d+\+?/g, '').replace(/(strength|dexterity|constitution|intelligence|wisdom|charisma|str|dex|con|int|wis|cha)\s*\d+\+?/g, '').replace(/\b(and|or|requires|score|of|higher)\b/g, '').replace(/[\s,;().:+-]/g, '');
    if (residual) return {
      allowed: false,
      reason: `Prerequisite needs verification: ${text}`
    };
  }
  if (!ignoreSlots && remainingFeatSlots(character) <= 0) return {
    allowed: false,
    reason: 'No earned feat selections remaining.'
  };
  return {
    allowed: true,
    reason: 'Available.',
    repeatable
  };
}
/** The highest score this feat can raise an ability to: 20, or 30 for an Epic Boon. */
export const featAbilityCap = d => Number(d?.abilityScoreMaximum || (String(d?.category).toLowerCase() === 'epic boon' || featKey(d).startsWith('boon of ') ? 30 : 20));
export function validateFeatAbilities(character, definition, selected) {
  const d = featRules(definition),
    amounts = Object.fromEntries(ABILITIES.map(a => [a, Number(selected[a] ?? 0)]));
  if (Object.values(amounts).some(n => !Number.isInteger(n) || n < 0)) return 'Ability increases must be nonnegative integers.';
  const base = Object.fromEntries(ABILITIES.map(a => [a, Number(d[a] || 0)]));
  const options = d.abilityIncreaseOptions || [];
  const walk = (i, allocation) => i === options.length ? ABILITIES.every(a => allocation[a] === amounts[a]) : (Array.isArray(options[i]) ? options[i] : options[i].abilities || []).some(a => ABILITIES.includes(a) && walk(i + 1, {
    ...allocation,
    [a]: allocation[a] + Number(Array.isArray(options[i]) ? 1 : options[i].amount ?? 1)
  }));
  if (!walk(0, base)) return 'Choose exactly the ability increases granted by this feat.';
  const cap = featAbilityCap(d);
  if (ABILITIES.some(a => amounts[a] > 0 && permanentAbilityScore(character, a) + amounts[a] > cap)) return `This feat cannot increase an ability score above ${cap}.`;
  return '';
}
const abilityModifier = score => Math.floor((score - 10) / 2);
/**
 * What a feat's ability picks would do, for the level-up window. `picks` holds the ability chosen for each entry of
 * `abilityIncreaseOptions`. `options[i]` lists that entry's abilities with the score before and after it, counting the
 * feat's fixed bonuses and the picks before it, so a second +1 to the same ability reads on from the first; `atMaximum`
 * means the increase would pass the cap. `result` lists each ability the feat raises, with its modifier before and after.
 */
export function featAbilityPreview(character, definition, picks = {}) {
  const d = featRules(definition), cap = featAbilityCap(d);
  const base = Object.fromEntries(ABILITIES.map(a => [a, permanentAbilityScore(character, a)]));
  const gains = Object.fromEntries(ABILITIES.map(a => [a, Number(d[a] || 0)]));
  const options = (d.abilityIncreaseOptions || []).map((option, index) => {
    const amount = Number(Array.isArray(option) ? 1 : option.amount ?? 1);
    const entries = (Array.isArray(option) ? option : option.abilities || []).filter(a => ABILITIES.includes(a)).map(ability => {
      const from = base[ability] + gains[ability], to = from + amount;
      return { ability, from, to, atMaximum: to > cap };
    });
    if (ABILITIES.includes(picks[index])) gains[picks[index]] += amount;
    return entries;
  });
  const result = ABILITIES.filter(a => gains[a] > 0).map(ability => {
    const from = base[ability], to = from + gains[ability];
    return { ability, from, to, fromModifier: abilityModifier(from), toModifier: abilityModifier(to) };
  });
  return { cap, options, result };
}
